import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';
import SchoolAvatar from '../components/SchoolAvatar';

function todayDateKey() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function emptyForm() {
  return {
    name: '',
    route_name: '',
    vehicle_number: '',
    capacity: '',
    send_onboarding: true,
    driver: {
      f_name: '',
      l_name: '',
      email: '',
      phone: '',
      driver_license_number: '',
      license_plate: '',
    },
  };
}

function formatLastLogin(value) {
  if (!value) return 'Never logged in';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Never logged in' : `Last login ${date.toLocaleString()}`;
}

export default function BusManagementPage({ token, schoolId }) {
  const [buses, setBuses] = useState([]);
  const [busDrivers, setBusDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [creating, setCreating] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedBusId, setSelectedBusId] = useState(null);
  const [rosterDate, setRosterDate] = useState(todayDateKey);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [updatingBusId, setUpdatingBusId] = useState(null);
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [driverEdit, setDriverEdit] = useState(null);
  const [savingDriver, setSavingDriver] = useState(false);
  const [assignmentEditor, setAssignmentEditor] = useState(null);
  const [rosterRefreshKey, setRosterRefreshKey] = useState(0);

  const loadBuses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [response, driversResponse] = await Promise.all([
        apiFetch(`/buses?school_id=${schoolId}`, {}, token),
        apiFetch(`/buses/drivers?school_id=${schoolId}`, {}, token),
      ]);
      const nextBuses = response.data || [];
      setBuses(nextBuses);
      setBusDrivers(driversResponse.data || []);
      setSelectedBusId((current) => (
        nextBuses.some((bus) => Number(bus.id) === Number(current))
          ? current
          : (nextBuses[0]?.id || null)
      ));
    } catch (err) {
      setError(err.message || 'Unable to load buses.');
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => {
    loadBuses();
  }, [loadBuses]);

  useEffect(() => {
    if (!selectedBusId || !rosterDate) {
      setRoster([]);
      return undefined;
    }
    let cancelled = false;
    setRosterLoading(true);
    apiFetch(`/buses/${selectedBusId}/roster?date=${encodeURIComponent(rosterDate)}`, {}, token)
      .then((response) => {
        if (!cancelled) setRoster(response.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Unable to load the bus roster.');
      })
      .finally(() => {
        if (!cancelled) setRosterLoading(false);
      });
    return () => { cancelled = true; };
  }, [rosterDate, rosterRefreshKey, selectedBusId, token]);

  const selectedBus = useMemo(
    () => buses.find((bus) => Number(bus.id) === Number(selectedBusId)) || null,
    [buses, selectedBusId]
  );

  const visibleAssignmentStudents = useMemo(() => {
    if (!assignmentEditor?.students) return [];
    const query = assignmentEditor.search.trim().toLowerCase();
    if (!query) return assignmentEditor.students;
    return assignmentEditor.students.filter((student) => (
      [student.f_name, student.l_name, `${student.f_name || ''} ${student.l_name || ''}`, student.grade]
        .some((value) => String(value ?? '').toLowerCase().includes(query))
    ));
  }, [assignmentEditor]);

  const updateDriverField = (field, value) => {
    setForm((current) => ({ ...current, driver: { ...current.driver, [field]: value } }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (creating) return;
    setCreating(true);
    setError('');
    setNotice('');
    try {
      const response = await apiFetch('/buses', {
        method: 'POST',
        body: JSON.stringify({
          school_id: Number(schoolId),
          name: form.name,
          route_name: form.route_name || null,
          vehicle_number: form.vehicle_number || null,
          capacity: form.capacity || null,
          driver: form.driver,
        }),
      }, token);
      const created = response.data;
      let onboardingNotice = '';
      if (form.send_onboarding && created?.driver_contact_id) {
        try {
          await apiFetch(`/contacts/${created.driver_contact_id}/onboarding-email`, {
            method: 'POST',
            body: JSON.stringify({ school_id: Number(schoolId), expires_in_hours: 72 }),
          }, token);
          onboardingNotice = ' The parent-app onboarding email was sent to the driver.';
        } catch (onboardingError) {
          onboardingNotice = ` The bus was created, but the onboarding email failed: ${onboardingError.message}`;
        }
      }
      setNotice(`Bus and driver account created.${onboardingNotice}`);
      setForm(emptyForm());
      setFormOpen(false);
      await loadBuses();
      setSelectedBusId(created?.id || null);
    } catch (err) {
      setError(err.message || 'Unable to create the bus and driver account.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (bus) => {
    setUpdatingBusId(bus.id);
    setError('');
    setNotice('');
    try {
      await apiFetch(`/buses/${bus.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !bus.is_active }),
      }, token);
      setNotice(`${bus.name} ${bus.is_active ? 'deactivated' : 'activated'}.`);
      await loadBuses();
    } catch (err) {
      setError(err.message || 'Unable to update the bus.');
    } finally {
      setUpdatingBusId(null);
    }
  };

  const openDriverEditor = (driver) => {
    setEditingDriverId(driver.id);
    setDriverEdit({
      f_name: driver.f_name || '',
      l_name: driver.l_name || '',
      email: driver.email || '',
      phone: driver.phone || '',
      driver_license_number: driver.driver_license_number || '',
      vehicle_make_model: driver.vehicle_make_model || '',
      car_color: driver.car_color || '',
      license_plate: driver.license_plate || '',
    });
    setError('');
    setNotice('');
  };

  const updateDriverEdit = (field, value) => {
    setDriverEdit((current) => ({ ...current, [field]: value }));
  };

  const handleSaveDriver = async (event) => {
    event.preventDefault();
    if (!editingDriverId || !driverEdit || savingDriver) return;
    setSavingDriver(true);
    setError('');
    setNotice('');
    try {
      await apiFetch(`/buses/drivers/${editingDriverId}`, {
        method: 'PUT',
        body: JSON.stringify({
          school_id: Number(schoolId),
          ...driverEdit,
          email: driverEdit.email.trim(),
          phone: driverEdit.phone.trim(),
          license_plate: driverEdit.license_plate.trim().toUpperCase(),
        }),
      }, token);
      setNotice('Bus driver updated.');
      setEditingDriverId(null);
      setDriverEdit(null);
      await loadBuses();
    } catch (err) {
      setError(err.message || 'Unable to update the bus driver.');
    } finally {
      setSavingDriver(false);
    }
  };

  const openAssignmentEditor = async (bus) => {
    setAssignmentEditor({
      busId: bus.id,
      busName: bus.name,
      capacity: bus.capacity,
      students: [],
      selectedIds: new Set(),
      search: '',
      loading: true,
      saving: false,
      error: '',
    });
    setError('');
    setNotice('');
    try {
      const response = await apiFetch(`/buses/${bus.id}/assignments`, {}, token);
      const students = response.data || [];
      setAssignmentEditor((current) => (
        current?.busId === bus.id
          ? {
            ...current,
            students,
            selectedIds: new Set(
              students
                .filter((student) => Number(student.assigned_bus_id) === Number(bus.id))
                .map((student) => Number(student.id))
            ),
            loading: false,
          }
          : current
      ));
    } catch (err) {
      setAssignmentEditor((current) => (
        current?.busId === bus.id
          ? { ...current, loading: false, error: err.message || 'Unable to load student assignments.' }
          : current
      ));
    }
  };

  const toggleAssignmentStudent = (studentId) => {
    setAssignmentEditor((current) => {
      if (!current || current.saving) return current;
      const selectedIds = new Set(current.selectedIds);
      if (selectedIds.has(Number(studentId))) selectedIds.delete(Number(studentId));
      else selectedIds.add(Number(studentId));
      return { ...current, selectedIds, error: '' };
    });
  };

  const selectVisibleAssignmentStudents = () => {
    setAssignmentEditor((current) => {
      if (!current || current.saving) return current;
      const selectedIds = new Set(current.selectedIds);
      visibleAssignmentStudents.forEach((student) => selectedIds.add(Number(student.id)));
      return { ...current, selectedIds, error: '' };
    });
  };

  const handleSaveAssignments = async () => {
    if (!assignmentEditor || assignmentEditor.saving) return;
    if (assignmentEditor.capacity !== null && assignmentEditor.selectedIds.size > Number(assignmentEditor.capacity)) {
      setAssignmentEditor((current) => ({
        ...current,
        error: `${current.busName} has capacity for ${current.capacity} students.`,
      }));
      return;
    }

    setAssignmentEditor((current) => ({ ...current, saving: true, error: '' }));
    try {
      const response = await apiFetch(`/buses/${assignmentEditor.busId}/assignments`, {
        method: 'PUT',
        body: JSON.stringify({ student_ids: [...assignmentEditor.selectedIds] }),
      }, token);
      const assignedCount = Number(response.data?.assigned_count ?? assignmentEditor.selectedIds.size);
      setAssignmentEditor(null);
      setNotice(`${assignmentEditor.busName} now has ${assignedCount} assigned student${assignedCount === 1 ? '' : 's'}.`);
      await loadBuses();
      setRosterRefreshKey((current) => current + 1);
    } catch (err) {
      setAssignmentEditor((current) => ({
        ...current,
        saving: false,
        error: err.message || 'Unable to update student assignments.',
      }));
    }
  };

  return (
    <div className="bus-page">
      <div className="bus-page-heading">
        <div>
          <h1>School Buses</h1>
          <p>Create school-wide bus drivers. Parents approve a default bus per child and can override individual days.</p>
        </div>
        <button type="button" className="sa-btn sa-btn-primary" onClick={() => setFormOpen((current) => !current)}>
          {formOpen ? 'Cancel' : 'Add Bus & Driver'}
        </button>
      </div>

      {error ? <div className="sa-error bus-banner">{error}</div> : null}
      {notice ? <div className="bus-notice bus-banner">{notice}</div> : null}

      {formOpen ? (
        <form className="bus-create-card" onSubmit={handleCreate}>
          <div className="bus-form-section">
            <h2>Bus details</h2>
            <div className="bus-form-grid">
              <label><span>Bus name *</span><input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Bus 12" required /></label>
              <label><span>Route name</span><input value={form.route_name} onChange={(event) => setForm((current) => ({ ...current, route_name: event.target.value }))} placeholder="North Route" /></label>
              <label><span>Vehicle number</span><input value={form.vehicle_number} onChange={(event) => setForm((current) => ({ ...current, vehicle_number: event.target.value }))} placeholder="12" /></label>
              <label><span>Capacity</span><input type="number" min="1" value={form.capacity} onChange={(event) => setForm((current) => ({ ...current, capacity: event.target.value }))} placeholder="48" /></label>
            </div>
          </div>

          <div className="bus-form-section">
            <h2>Driver parent-app account</h2>
            <p>The driver receives a restricted driver account. Its location triggers the geofence for that day’s bus roster.</p>
            <div className="bus-form-grid">
              <label><span>First name *</span><input value={form.driver.f_name} onChange={(event) => updateDriverField('f_name', event.target.value)} required /></label>
              <label><span>Last name *</span><input value={form.driver.l_name} onChange={(event) => updateDriverField('l_name', event.target.value)} required /></label>
              <label><span>Email *</span><input type="email" value={form.driver.email} onChange={(event) => updateDriverField('email', event.target.value)} required /></label>
              <label><span>Mobile phone *</span><input type="tel" value={form.driver.phone} onChange={(event) => updateDriverField('phone', event.target.value)} required /></label>
              <label><span>Driver license</span><input value={form.driver.driver_license_number} onChange={(event) => updateDriverField('driver_license_number', event.target.value)} /></label>
              <label><span>Bus license plate</span><input value={form.driver.license_plate} onChange={(event) => updateDriverField('license_plate', event.target.value)} /></label>
            </div>
            <label className="bus-checkbox">
              <input type="checkbox" checked={form.send_onboarding} onChange={(event) => setForm((current) => ({ ...current, send_onboarding: event.target.checked }))} />
              <span>Send the parent-app onboarding email after creation</span>
            </label>
          </div>

          <div className="bus-form-actions">
            <button type="submit" className="sa-btn sa-btn-primary" disabled={creating}>
              {creating ? 'Creating…' : 'Create Bus & Driver Account'}
            </button>
          </div>
        </form>
      ) : null}

      {loading ? <div className="sa-empty">Loading buses…</div> : (
        <>
        <div className="bus-layout">
          <section className="bus-directory">
            <div className="bus-section-heading">
              <h2>Buses</h2>
              <span>{buses.length}</span>
            </div>
            {buses.length ? buses.map((bus) => (
              <button
                type="button"
                key={bus.id}
                className={`bus-list-item ${Number(selectedBusId) === Number(bus.id) ? 'active' : ''}`}
                onClick={() => setSelectedBusId(bus.id)}
              >
                <div className="bus-list-copy">
                  <strong>{bus.name}</strong>
                  <span>{bus.route_name || bus.vehicle_number || 'Route not specified'}</span>
                </div>
                <div className="bus-list-meta">
                  <span>{bus.assignment_count} assigned</span>
                  <span className={bus.is_active ? 'bus-status-active' : 'bus-status-inactive'}>{bus.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </button>
            )) : <div className="sa-empty">No buses have been created.</div>}
          </section>

          <section className="bus-detail-card">
            {selectedBus ? (
              <>
                <div className="bus-detail-header">
                  <div>
                    <h2>{selectedBus.name}</h2>
                    <p>{[selectedBus.route_name, selectedBus.vehicle_number && `Vehicle ${selectedBus.vehicle_number}`].filter(Boolean).join(' · ') || 'No route details'}</p>
                  </div>
                  <div className="bus-detail-actions">
                    <button type="button" className="sa-btn sa-btn-primary" onClick={() => openAssignmentEditor(selectedBus)}>
                      Assign Students
                    </button>
                    <button type="button" className="sa-btn sa-btn-secondary" disabled={updatingBusId === selectedBus.id} onClick={() => handleToggleActive(selectedBus)}>
                      {selectedBus.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>

                <div className="bus-driver-card">
                  <SchoolAvatar record={{ id: selectedBus.driver_contact_id, f_name: selectedBus.driver_f_name, l_name: selectedBus.driver_l_name }} name={selectedBus.driver_name} className="bus-driver-avatar" />
                  <div>
                    <strong>{selectedBus.driver_name}</strong>
                    <span>{selectedBus.driver_email}</span>
                    <span>{selectedBus.driver_phone}</span>
                    <small>{selectedBus.has_password ? formatLastLogin(selectedBus.driver_last_login_at) : 'Account has not been claimed'}</small>
                  </div>
                </div>

                <div className="bus-roster-heading">
                  <div>
                    <h3>Effective roster</h3>
                    <p>Daily parent overrides are reflected here and in the driver’s geofence roster.</p>
                  </div>
                  <input type="date" value={rosterDate} onChange={(event) => setRosterDate(event.target.value)} />
                </div>
                {rosterLoading ? <div className="sa-empty">Loading roster…</div> : roster.length ? (
                  <div className="bus-roster-list">
                    {roster.map((student) => (
                      <div className="bus-roster-row" key={student.id}>
                        <SchoolAvatar record={student} name={`${student.f_name} ${student.l_name}`} />
                        <strong>{student.f_name} {student.l_name}</strong>
                        <span>Grade {student.grade}</span>
                      </div>
                    ))}
                  </div>
                ) : <div className="sa-empty">No students are riding this bus on this date.</div>}
              </>
            ) : <div className="sa-empty">Select a bus to view its driver and roster.</div>}
          </section>
        </div>
        <section className="bus-driver-directory">
          <div className="bus-section-heading">
            <div>
              <h2>Bus Drivers</h2>
              <p>School-wide drivers assigned to one or more buses.</p>
            </div>
            <span>{busDrivers.length}</span>
          </div>

          {busDrivers.length ? (
            <div className="bus-driver-directory-list">
              {busDrivers.map((driver) => (
                <div className="bus-driver-directory-item" key={driver.id}>
                  <SchoolAvatar record={driver} name={`${driver.f_name || ''} ${driver.l_name || ''}`.trim()} className="bus-driver-avatar" />
                  <div className="bus-driver-directory-copy">
                    <strong>{driver.f_name} {driver.l_name}</strong>
                    <span>{driver.email || 'No email'} · {driver.phone || 'No phone'}</span>
                    <span>{driver.bus_names || 'No assigned bus'}</span>
                    <small>{driver.has_password ? formatLastLogin(driver.last_login_at) : 'Account has not been claimed'}</small>
                  </div>
                  <button type="button" className="sa-btn sa-btn-secondary" onClick={() => openDriverEditor(driver)}>Edit</button>
                </div>
              ))}
            </div>
          ) : <div className="sa-empty">No bus drivers have been created.</div>}

          {editingDriverId && driverEdit ? (
            <form className="bus-driver-edit-form" onSubmit={handleSaveDriver}>
              <div className="bus-driver-edit-heading">
                <div>
                  <h3>Edit Bus Driver</h3>
                  <p>Changes apply anywhere this school bus driver is used.</p>
                </div>
                <button type="button" className="sa-btn sa-btn-secondary" onClick={() => { setEditingDriverId(null); setDriverEdit(null); }} disabled={savingDriver}>Cancel</button>
              </div>
              <div className="bus-form-grid">
                <label><span>First name *</span><input value={driverEdit.f_name} onChange={(event) => updateDriverEdit('f_name', event.target.value)} required disabled={savingDriver} /></label>
                <label><span>Last name *</span><input value={driverEdit.l_name} onChange={(event) => updateDriverEdit('l_name', event.target.value)} required disabled={savingDriver} /></label>
                <label><span>Email *</span><input type="email" value={driverEdit.email} onChange={(event) => updateDriverEdit('email', event.target.value)} required disabled={savingDriver} /></label>
                <label><span>Mobile phone *</span><input type="tel" value={driverEdit.phone} onChange={(event) => updateDriverEdit('phone', event.target.value)} required disabled={savingDriver} /></label>
                <label><span>Driver license</span><input value={driverEdit.driver_license_number} onChange={(event) => updateDriverEdit('driver_license_number', event.target.value)} disabled={savingDriver} /></label>
                <label><span>Vehicle make/model</span><input value={driverEdit.vehicle_make_model} onChange={(event) => updateDriverEdit('vehicle_make_model', event.target.value)} disabled={savingDriver} /></label>
                <label><span>Vehicle color</span><input value={driverEdit.car_color} onChange={(event) => updateDriverEdit('car_color', event.target.value)} disabled={savingDriver} /></label>
                <label><span>License plate</span><input value={driverEdit.license_plate} onChange={(event) => updateDriverEdit('license_plate', event.target.value.toUpperCase())} disabled={savingDriver} /></label>
              </div>
              <div className="bus-form-actions">
                <button type="submit" className="sa-btn sa-btn-primary" disabled={savingDriver}>{savingDriver ? 'Saving…' : 'Save Driver'}</button>
              </div>
            </form>
          ) : null}
        </section>
        </>
      )}

      {assignmentEditor ? (
        <div className="bus-assignment-overlay" onClick={() => !assignmentEditor.saving && setAssignmentEditor(null)}>
          <div className="bus-assignment-modal" onClick={(event) => event.stopPropagation()}>
            <div className="bus-assignment-heading">
              <div>
                <h2>Assign Students</h2>
                <p>{assignmentEditor.busName} · {assignmentEditor.selectedIds.size} selected{assignmentEditor.capacity ? ` of ${assignmentEditor.capacity}` : ''}</p>
              </div>
              <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setAssignmentEditor(null)} disabled={assignmentEditor.saving}>Close</button>
            </div>

            {assignmentEditor.error ? <div className="sa-error bus-assignment-error">{assignmentEditor.error}</div> : null}

            <div className="bus-assignment-tools">
              <input
                type="search"
                value={assignmentEditor.search}
                onChange={(event) => setAssignmentEditor((current) => ({ ...current, search: event.target.value }))}
                placeholder="Search students by name or grade"
                disabled={assignmentEditor.saving}
              />
              <button type="button" className="sa-btn sa-btn-secondary" onClick={selectVisibleAssignmentStudents} disabled={assignmentEditor.loading || assignmentEditor.saving || !visibleAssignmentStudents.length}>
                Select Visible
              </button>
              <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setAssignmentEditor((current) => ({ ...current, selectedIds: new Set(), error: '' }))} disabled={assignmentEditor.saving || !assignmentEditor.selectedIds.size}>
                Clear All
              </button>
            </div>

            <p className="bus-assignment-help">Selecting a student assigned to another bus moves their default assignment here. Removing a student also clears their future daily bus overrides.</p>

            <div className="bus-assignment-list">
              {assignmentEditor.loading ? <div className="sa-empty">Loading students…</div> : visibleAssignmentStudents.length ? (
                visibleAssignmentStudents.map((student) => {
                  const selected = assignmentEditor.selectedIds.has(Number(student.id));
                  const assignedElsewhere = student.assigned_bus_id && Number(student.assigned_bus_id) !== Number(assignmentEditor.busId);
                  return (
                    <label className={`bus-assignment-row ${selected ? 'selected' : ''}`} key={student.id}>
                      <input type="checkbox" checked={selected} onChange={() => toggleAssignmentStudent(student.id)} disabled={assignmentEditor.saving} />
                      <SchoolAvatar record={student} name={`${student.f_name} ${student.l_name}`} />
                      <span className="bus-assignment-student-copy">
                        <strong>{student.f_name} {student.l_name}</strong>
                        <small>Grade {student.grade}{assignedElsewhere ? ` · Currently on ${student.assigned_bus_name}` : ''}</small>
                      </span>
                    </label>
                  );
                })
              ) : <div className="sa-empty">No students match this search.</div>}
            </div>

            <div className="bus-assignment-footer">
              <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setAssignmentEditor(null)} disabled={assignmentEditor.saving}>Cancel</button>
              <button type="button" className="sa-btn sa-btn-primary" onClick={handleSaveAssignments} disabled={assignmentEditor.loading || assignmentEditor.saving}>
                {assignmentEditor.saving ? 'Saving…' : `Save ${assignmentEditor.selectedIds.size} Students`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
